import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useMemo } from "react";
import GlobalStoreContext from "../../context/GlobalStoreContext";
import { CalendarEvent } from "../../models/CalendarEvent";
import { Delivery } from "../../models/Delivery";
import TeamInfo from "../../teamInfo";
import TeamAvatar from "../Team/teamAvatar";

interface DamnInformationSectionProps {
    calendarEvent: CalendarEvent;
    delivery: Delivery;
    gcTeam: TeamInfo | null;
    receivingTeam: TeamInfo | null;
}

const DamnInformationSection: React.FC<DamnInformationSectionProps> = (props) => {
    const context = React.useContext(GlobalStoreContext);

    const [expandedSection, setExpandedSection] = React.useState<boolean>(true);

    const deliveryEquipment = useMemo(
        () =>
            context
                .equipmentForProject(context.currentProjectInfo.unique_token)
                .filter(
                    (eq) => props.calendarEvent.deliveryEquipmentIds().includes(eq.id) && eq.equipmentName !== undefined
                ),
        [props.calendarEvent, context.equipmentByProject]
    );

    return (
        <section className="mx-4 mt-4">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
                <button
                    onClick={() => setExpandedSection(!expandedSection)}
                    className="w-full cursor-pointer p-5 flex items-center justify-between hover:bg-linear-to-r hover:from-gray-50 hover:to-white transition-all"
                >
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Delivery Information</h3>
                    <div
                        className={`p-1.5 rounded-lg transition-all ${
                            expandedSection ? "bg-plot-orange/10" : "bg-gray-100"
                        }`}
                    >
                        {expandedSection ? (
                            <FontAwesomeIcon icon={faChevronUp} className="text-plot-orange" />
                        ) : (
                            <FontAwesomeIcon icon={faChevronDown} className="text-plot-orange" />
                        )}
                    </div>
                </button>

                {expandedSection && (
                    <div className="px-5 pb-5 space-y-3 bg-linear-to-b from-white to-gray-50">
                        <div className="flex py-3 border-b border-gray-200 justify-between">
                            <div className="text-sm font-medium text-gray-600 w-1/3">General Contractor:</div>
                            <div className="text-sm text-gray-900">
                                <TeamAvatar team={props.gcTeam} size={40} showName={true} />
                            </div>
                        </div>
                        <div className="flex py-3 border-b border-gray-200 justify-between">
                            <div className="text-sm font-medium text-gray-600 w-1/3">Receiving Contractor:</div>
                            <div className="text-sm text-gray-900">
                                <TeamAvatar team={props.receivingTeam} size={40} showName={true} />
                            </div>
                        </div>
                        <div className="flex justify-between py-3 border-b border-gray-200">
                            <span className="text-sm font-medium text-gray-600">Unloading Equipment:</span>
                            <span className="text-sm text-gray-700 text-right max-w-[60%]">
                                {deliveryEquipment && deliveryEquipment.length > 0
                                    ? deliveryEquipment.map((equipment) => equipment?.equipmentName).join(", ")
                                    : "None Specified"}
                            </span>
                        </div>

                        <div className="flex justify-between py-3 border-b border-gray-200">
                            <span className="text-sm font-medium text-gray-600">Delivery Status:</span>
                            <span className="uppercase inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-linear-to-r from-green-500 to-green-600 text-white shadow-sm">
                                {props.delivery.status}
                            </span>
                        </div>
                        <div className="flex justify-between py-3">
                            <span className="text-sm font-medium text-gray-600">Delivery Window:</span>
                            <div className="text-right">
                                <p className="text-sm font-bold text-gray-900">
                                    {props.calendarEvent.startAt.format("MM/DD/YYYY")}
                                </p>
                                <p className="text-xs text-gray-600 font-medium">
                                    {props.calendarEvent.startAt.format("hh:mm A")} -{" "}
                                    {props.calendarEvent.endAt.format("hh:mm A")}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default DamnInformationSection;
